/* eslint-disable no-async-promise-executor */

import { ClearAllCacheFromIndexedDB } from "@/IndexedDB/cacheIndexedDB";
import { CheckIndexedDBIsSupport } from "@/IndexedDB/tabIndexedDB";
import { SetMerchantChannel } from "@/broadcastChannel/merchantChannel";
import { CheckBroadcastIsSupport } from "@/broadcastChannel/tabChannel";
import { BroadcastReCreateTab, SetUserChannel } from "@/broadcastChannel/userChannel";
import {
  ActivityLogSource,
  ApplipcationStoreType,
  AuthenticationResponseCode,
  LoadingStatusType,
  Locales,
  TwoFactorAuthenticationType,
  TypeComponentNoti,
  UserType,
} from "@/enums/enums";
import {
  AddAccessTokenCache,
  AddSelectedMerchantCache,
  AddUserProfileCache,
  DeleteSelectedMerchantCache,
  GetSelectedMerchantFromCache,
  GetUserProfileFromCache,
} from "@/helper/cacheHelper";
import { GetErrorMessageByExceptionCode } from "@/helper/exceptionCodeHelper";
import {
  CompareMerchantNameEn,
  CompareMerchantNameTh,
  ConvertToMerchantList,
  GetSelectedMerchant,
} from "@/helper/merchantHelper";
import { SocketConnect } from "@/helper/notificationHelper";
import { ClearMasterTabFromCookies, InsertTabIdToTabDB, UpdateTabMerchantId } from "@/helper/tabHelper";
import {
  MerchantId,
  RefreshToken,
  RemoveCookiesToken,
  SetCookiesAccessToken,
  SetCookiesKeepLogin,
  SetCookiesRefreshToken,
  UserId,
} from "@/helper/userHelper";
import { i18n } from "@/i18n";
import { MerchantInFoModel } from "@/model/merchant/MerchantInFoModel";
import { SelectedMerchantModel } from "@/model/merchant/selectedMerchantModel";
import { UserModel } from "@/model/user/userModel";
import router from "@/router/index";
import API from "@/service/Index";
import { DatadogRumService } from "@/service/datadog/datadogRumService";
import merchantService from "@/service/merchant/merchantService";
import { ConvertToUserModel } from "@/service/user/authenFacade";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import TextHelper from "../../helper/textHelper";

@Module({ namespaced: true })
class LogIn extends VuexModule {
  public loginLoadingStatus = LoadingStatusType.None;
  public userModel: UserModel | null = null;
  public merchantList = [] as Array<MerchantInFoModel>;
  public merchantListLoadingStatus: LoadingStatusType = LoadingStatusType.None;
  public selectMerchant: SelectedMerchantModel | null = null;
  public setSelectMerchantLoadingStatus: LoadingStatusType = LoadingStatusType.None;
  public refreshTokenExpirationLodingStatus: LoadingStatusType = LoadingStatusType.None;
  public merchantModelByIdLoadingStatus: LoadingStatusType = LoadingStatusType.None;
  public merchantModelListForMap: Map<number, { merchant: SelectedMerchantModel }> = new Map();
  public favoriteMerchantList: any = [];
  public favoriteMerchantListLoadingStatus: LoadingStatusType = LoadingStatusType.None;
  public updateFavoriteMerchantLoadingStatus: LoadingStatusType = LoadingStatusType.None;
  public deleteFavoriteMerchantLoadingStatus: LoadingStatusType = LoadingStatusType.None;
  public merchantByResetCacheLoadingStatus: LoadingStatusType = LoadingStatusType.None;
  public getUserInfoForInvalidPINLoadingStatus: LoadingStatusType = LoadingStatusType.None;
  public twoFactorAuthData: any = null;
  public originalLoginRequest: any = null;
  public twoFactorErrorMessage: string = "";
  public verifyNextDelay: number = 0;
  public loginResponseData: any = null;

  get getLoginLoadingStatus(): LoadingStatusType {
    return this.loginLoadingStatus;
  }

  get getMerchantList(): Array<MerchantInFoModel> {
    const merchantListMockup = [...this.merchantList];
    if (i18n.locale == Locales.TH) {
      return merchantListMockup.sort(CompareMerchantNameTh);
    } else {
      return merchantListMockup.sort(CompareMerchantNameEn);
    }
  }
  get getMerchantListLoadingStatus(): LoadingStatusType {
    return this.merchantListLoadingStatus;
  }
  get getUserModel(): UserModel | null {
    return this.userModel;
  }
  get getMerchant(): SelectedMerchantModel | null {
    return this.selectMerchant;
  }
  get getSetSelectMerchantLoadingStatus(): any {
    return this.setSelectMerchantLoadingStatus;
  }

  get getMerchantModelListForMap() {
    return this.merchantModelListForMap;
  }

  get getMerchantModelByIdLoadingStatus() {
    return this.merchantModelByIdLoadingStatus;
  }

  get getFavoriteMerchantList() {
    return this.favoriteMerchantList;
  }

  get getFavoriteMerchantListLoadingStatus() {
    return this.favoriteMerchantListLoadingStatus;
  }

  get getUpdateFavoriteMerchantLoadingStatus() {
    return this.updateFavoriteMerchantLoadingStatus;
  }

  get getDeleteFavoriteMerchantLoadingStatus() {
    return this.deleteFavoriteMerchantLoadingStatus;
  }

  get getMerchantByResetCacheLoadingStatus() {
    return this.merchantByResetCacheLoadingStatus;
  }

  get getGetUserInfoForInvalidPINLoadingStatus() {
    return this.getUserInfoForInvalidPINLoadingStatus;
  }

  get getTwoFactorAuthData() {
    return this.twoFactorAuthData;
  }

  get getOriginalLoginRequest() {
    return this.originalLoginRequest;
  }

  get getTwoFactorErrorMessage() {
    return this.twoFactorErrorMessage;
  }

  get getVerifyNextDelay() {
    return this.verifyNextDelay;
  }

  get getLoginResponseData() {
    return this.loginResponseData;
  }

  @Mutation
  public MutationLoginLoadingStatus(status: LoadingStatusType): void {
    this.loginLoadingStatus = status;
  }

  @Mutation
  public MutationsSelectMerchant(_object: SelectedMerchantModel): void {
    this.selectMerchant = _object;
  }

  @Mutation
  public MutationsSetSelectMerchantLoadingStatus(status: LoadingStatusType): void {
    this.setSelectMerchantLoadingStatus = status;
  }

  @Mutation
  public MutationSetUserModel(userModel: UserModel) {
    this.userModel = userModel;
    sessionStorage.setItem("eui", userModel.userId.PeakEncryption());
  }

  @Mutation
  public MutationSetMerchantsList(merchantsList: Array<MerchantInFoModel>) {
    this.merchantList = merchantsList;
  }

  @Mutation
  public MutationSetMerchantListLoadingStatus(status: LoadingStatusType) {
    this.merchantListLoadingStatus = status;
  }

  @Mutation
  public MutationsSetRefreshTokenExpirationLodingStatus(status: LoadingStatusType) {
    this.refreshTokenExpirationLodingStatus = status;
  }
  @Mutation
  MutationSetFavoriteMerchantList(favoriteMerchantList: any) {
    this.favoriteMerchantList = favoriteMerchantList;
  }

  @Mutation
  MutationSetFavoriteMerchantListLoadingStatus(status: LoadingStatusType) {
    this.favoriteMerchantListLoadingStatus = status;
  }

  @Mutation
  MutationSetUpdateFavoriteMerchantLoadingStatus(status: LoadingStatusType) {
    this.updateFavoriteMerchantLoadingStatus = status;
  }

  @Mutation
  MutationSetDeleteFavoriteMerchantLoadingStatus(status: LoadingStatusType) {
    this.deleteFavoriteMerchantLoadingStatus = status;
  }

  @Mutation
  public MutationSetMerchantModelByIdLoadingStatus(status: LoadingStatusType) {
    this.merchantModelByIdLoadingStatus = status;
  }

  @Mutation
  MutationSetMerchantModelList(input: { merchantId: number; merchant: SelectedMerchantModel }) {
    this.merchantModelListForMap.set(input.merchantId, { merchant: input.merchant });
  }

  @Mutation
  MutationReSetMerchantModelList() {
    this.merchantModelListForMap = new Map();
  }

  @Mutation
  MutationsGetMerchantByResetCacheLoadingStatus(status: LoadingStatusType) {
    this.merchantByResetCacheLoadingStatus = status;
  }

  @Mutation
  MutationSetGetUserInfoForInvalidPINLoadingStatus(status: LoadingStatusType) {
    this.getUserInfoForInvalidPINLoadingStatus = status;
  }

  @Mutation
  MutationSetTwoFactorAuthData(twoFactorAuthData: any) {
    this.twoFactorAuthData = twoFactorAuthData;
  }

  @Mutation
  MutationSetOriginalLoginRequest(originalLoginRequest: any) {
    this.originalLoginRequest = originalLoginRequest;
  }

  @Mutation
  MutationSetTwoFactorErrorMessage(message: string) {
    this.twoFactorErrorMessage = message;
  }

  @Mutation
  MutationSetVerifyNextDelay(delay: number) {
    this.verifyNextDelay = delay;
  }

  @Mutation
  MutationSetLoginResponseData(data: any) {
    this.loginResponseData = data;
  }

  @Action
  public cleanLogin() {
    return API.AuthenService.DoGetNewAccessTokenByRefreshToken().then(async (res) => {
      if (res.status.IsSuccess()) {
        const idToken = res.data.id_token;
        const userId: any = this.userModel?.userId.PeakEncryption();

        await ClearAllCacheFromIndexedDB();
        await AddAccessTokenCache(res.data.access_token);

        return API.AuthenService.DoRevokeToken(idToken, userId).then((resRevokeToken) => {
          this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.None);
          RemoveCookiesToken();
          sessionStorage.removeItem("emi");
          return resRevokeToken;
        });
      } else {
        this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.None);
        RemoveCookiesToken();
        sessionStorage.removeItem("emi");
        return res;
      }
    });
  }

  @Action
  public DoLogIn(_input: any): void {
    this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.Loading);
    let encpw: string = _input.password;

    if (_input.userType == UserType.Email) {
      encpw = TextHelper.CreateToken(_input.password);
    }

    const requestInput = {
      userName: _input.username,
      password: encpw,
      userType: _input.userType,
      iss: _input.iss || "",
    };

    // เก็บข้อมูล login request ต้นฉบับ
    const originalRequest = {
      username: _input.username,
      password: encpw,
      userType: _input.userType,
      keepLogin: _input.keepLogin,
      iss: _input.iss || "",
    };
    this.context.commit("MutationSetOriginalLoginRequest", originalRequest);

    const source = ActivityLogSource.PeakPayroll;
    API.AuthenService.DoGetAccessToken(requestInput, source).then(async (resAccessToken) => {
      // ตรวจสอบ response_code ก่อน เพื่อจัดการ 2FA
      const responseCode = resAccessToken.data.response_code ? resAccessToken.data.response_code : 200;

      if (responseCode === AuthenticationResponseCode.RequireEmailOTP) {
        // ตรวจสอบว่าเป็น invalid_otp หรือไม่
        if (resAccessToken.data.invalid_otp === true) {
          this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.None);
          return; // ไม่ทำอะไรเพิ่มเติม
        }

        // เก็บ reference_otp ใน cookie เมื่อได้ response_code 201
        if (resAccessToken.data.reference_otp && resAccessToken.data.reference_otp_live) {
          const { setReferenceOtpCookie } = await import("@/helper/twoFactorAuthHelper");
          setReferenceOtpCookie(
            _input.username,
            resAccessToken.data.reference_otp,
            resAccessToken.data.reference_otp_live
          );
        }

        // เก็บข้อมูลสำหรับ 2FA
        const twoFactorData = {
          type: TwoFactorAuthenticationType.Email,
          referenceOtp: resAccessToken.data.reference_otp || "",
          recoveryCodeAttempt: resAccessToken.data.recovery_code_attempt || 0,
        };
        this.context.commit("MutationSetTwoFactorAuthData", twoFactorData);
        this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.None);
      } else if (responseCode === AuthenticationResponseCode.RequireAuthenticatorOTP) {
        // RequireAuthenticatorOTP
        // เปิด ModalSignInTwoFactorAuth สำหรับ Authenticator OTP
        const twoFactorData = {
          type: TwoFactorAuthenticationType.Authenticator,
          referenceOtp: resAccessToken.data.reference_otp || "",
          recoveryCodeAttempt: resAccessToken.data.recovery_code_attempt || 0,
        };
        this.context.commit("MutationSetTwoFactorAuthData", twoFactorData);
        this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.None);
      } else if (responseCode === AuthenticationResponseCode.EmailOTPAlreadyRequested) {
        // EmailOTPAlreadyRequested
        // ตรวจสอบ cookie สำหรับ reference_otp เมื่อได้ response_code 303
        let referenceOtp = resAccessToken.data.reference_otp;
        if (!referenceOtp || referenceOtp === null) {
          const { getReferenceOtpCookie, isReferenceOtpValid } = await import("@/helper/twoFactorAuthHelper");
          const cookieData = getReferenceOtpCookie(_input.username);
          if (cookieData && isReferenceOtpValid(_input.username)) {
            referenceOtp = cookieData.referenceOtp;
          }
        }

        // เก็บข้อมูลสำหรับ 2FA
        const twoFactorData = {
          type: TwoFactorAuthenticationType.Email,
          referenceOtp: referenceOtp || "",
          recoveryCodeAttempt: resAccessToken.data.recovery_code_attempt || 0,
        };
        this.context.commit("MutationSetTwoFactorAuthData", twoFactorData);
        this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.None);
      } else if (responseCode.IsSuccess()) {
        const token = {
          accessToken: resAccessToken.data.access_token,
          refreshToken: resAccessToken.data.refresh_token,
        };
        await AddAccessTokenCache(resAccessToken.data.access_token);

        if (_input.keepLogin) {
          SetCookiesKeepLogin();
          SetCookiesAccessToken(token.accessToken);
          SetCookiesRefreshToken(token.refreshToken, true);
        } else {
          SetCookiesAccessToken(token.accessToken);
          SetCookiesRefreshToken(token.refreshToken);
        }

        API.AuthenService.DoGetUserInfo().then(async (resUserInfo) => {
          if (resUserInfo.status.IsSuccess()) {
            DatadogRumService.SetUserFromUserInfo(resUserInfo?.data?.userInfo);
            const userModel: UserModel = ConvertToUserModel(resUserInfo.data.userInfo);
            await AddUserProfileCache(userModel.userId, userModel);
            this.context.commit("MutationSetUserModel", userModel);
            this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.Success);
            ClearMasterTabFromCookies();
            if (CheckBroadcastIsSupport() && CheckIndexedDBIsSupport()) {
              await InsertTabIdToTabDB();
              SetUserChannel(userModel.userId);
              BroadcastReCreateTab();
            }
            const lastUrl = localStorage.getItem("lastUrlPeakPayroll");
            if (lastUrl) {
              ClearMasterTabFromCookies();
              localStorage.removeItem("lastUrlPeakPayroll");
              window.location.href = lastUrl;
            } else {
              router.push({ name: "selectList" });
            }
          } else {
            this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.Success);
            router.push("/logout");
          }
        });
      } else if (responseCode === AuthenticationResponseCode.RequireEmailOTP) {
        // ตรวจสอบว่าเป็น invalid_otp หรือไม่
        if (resAccessToken.data.invalid_otp === true) {
          // OTP ผิด - ไม่ปิด modal แต่เคลียร์ OTP และแสดง alert
          this.context.dispatch(
            "Alert/SetAlertMessage",
            {
              alertMessage: i18n.t("Security_Setting.Modal_Two_Factor_Authentication_Enter_Code_Error_OTP"),
              typeMessage: TypeComponentNoti.error,
            },
            { root: true }
          );
          this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.None);
          return; // ไม่ทำอะไรเพิ่มเติม
        }

        // เก็บ reference_otp ใน cookie เมื่อได้ response_code 201
        if (resAccessToken.data.reference_otp && resAccessToken.data.reference_otp_live) {
          const { setReferenceOtpCookie } = await import("@/helper/twoFactorAuthHelper");
          setReferenceOtpCookie(
            _input.username,
            resAccessToken.data.reference_otp,
            resAccessToken.data.reference_otp_live
          );
        }

        // เก็บข้อมูลสำหรับ 2FA
        const twoFactorData = {
          type: TwoFactorAuthenticationType.Email,
          referenceOtp: resAccessToken.data.reference_otp || "",
        };
        this.context.commit("MutationSetTwoFactorAuthData", twoFactorData);
        this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.None);
      } else if (responseCode === AuthenticationResponseCode.RequireAuthenticatorOTP) {
        // RequireAuthenticatorOTP
        // เปิด ModalSignInTwoFactorAuth สำหรับ Authenticator OTP
        const twoFactorData = {
          type: TwoFactorAuthenticationType.Authenticator,
          referenceOtp: resAccessToken.data.reference_otp || "",
        };
        this.context.commit("MutationSetTwoFactorAuthData", twoFactorData);
        this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.None);
      } else if (responseCode === AuthenticationResponseCode.EmailOTPAlreadyRequested) {
        // EmailOTPAlreadyRequested
        // ตรวจสอบ cookie สำหรับ reference_otp เมื่อได้ response_code 303
        let referenceOtp = resAccessToken.data.reference_otp;
        if (!referenceOtp || referenceOtp === null) {
          const { getReferenceOtpCookie, isReferenceOtpValid } = await import("@/helper/twoFactorAuthHelper");
          const cookieData = getReferenceOtpCookie(_input.username);
          if (cookieData && isReferenceOtpValid(_input.username)) {
            referenceOtp = cookieData.referenceOtp;
          }
        }

        // แสดง alert message สำหรับ Email OTP ที่ถูกส่งไปแล้ว
        this.context.dispatch(
          "Alert/SetAlertMessage",
          {
            alertMessage: i18n.t("Security_Setting.Modal_Two_Factor_Authentication_Enter_Code_Email"),
            typeMessage: TypeComponentNoti.Success,
          },
          { root: true }
        );

        // เก็บข้อมูลสำหรับ 2FA
        const twoFactorData = {
          type: TwoFactorAuthenticationType.Email,
          referenceOtp: referenceOtp || "",
        };
        this.context.commit("MutationSetTwoFactorAuthData", twoFactorData);
        this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.None);
      } else if (responseCode.IsSuccess()) {
        // Login สำเร็จ
        const token = {
          accessToken: resAccessToken.data.access_token,
          refreshToken: resAccessToken.data.refresh_token,
        };
        await AddAccessTokenCache(resAccessToken.data.access_token);

        if (_input.keepLogin) {
          SetCookiesKeepLogin();
          SetCookiesAccessToken(token.accessToken);
          SetCookiesRefreshToken(token.refreshToken, true);
        } else {
          SetCookiesAccessToken(token.accessToken);
          SetCookiesRefreshToken(token.refreshToken);
        }

        API.AuthenService.DoGetUserInfo().then(async (resUserInfo) => {
          if (resUserInfo.status.IsSuccess()) {
            DatadogRumService.SetUserFromUserInfo(resUserInfo?.data?.userInfo);
            const userModel: UserModel = ConvertToUserModel(resUserInfo.data.userInfo);
            await AddUserProfileCache(userModel.userId, userModel);
            this.context.commit("MutationSetUserModel", userModel);
            this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.Success);
            ClearMasterTabFromCookies();
            if (CheckBroadcastIsSupport() && CheckIndexedDBIsSupport()) {
              await InsertTabIdToTabDB();
              SetUserChannel(userModel.userId);
              BroadcastReCreateTab();
            }
            const lastUrl = localStorage.getItem("lastUrlPeakPayroll");
            if (lastUrl) {
              ClearMasterTabFromCookies();
              localStorage.removeItem("lastUrlPeakPayroll");
              window.location.href = lastUrl;
            } else {
              router.push({ name: "selectList" });
            }
          } else {
            this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.Success);
            router.push("/logout");
          }
        });
      } else {
        // Login ล้มเหลว
        this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.Fail);
        RemoveCookiesToken();
      }
    });
  }

  @Action
  public async AuthorizeModels(routerName: string): Promise<void> {
    const refreshToken = RefreshToken();
    if (refreshToken) {
      const userProfileLocal: UserModel | null = this.userModel;
      if (userProfileLocal === null) {
        const userProfileCache: UserModel | null = await GetUserProfileFromCache(UserId());

        if (userProfileCache && userProfileCache.userId > 0) {
          this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.Loading);

          setTimeout(async () => {
            this.context.commit("MutationSetUserModel", userProfileCache);
            if (CheckBroadcastIsSupport() && CheckIndexedDBIsSupport()) {
              await InsertTabIdToTabDB();
              SetUserChannel(userProfileCache.userId);
            }
            SocketConnect();
            this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.Success);
          });
        } else {
          this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.Loading);
          try {
            API.AuthenService.DoGetUserInfo().then(async (resUserInfo) => {
              if (resUserInfo.status.IsSuccess()) {
                DatadogRumService.SetUserFromUserInfo(resUserInfo?.data?.userInfo);
                const userModel: UserModel = ConvertToUserModel(resUserInfo.data.userInfo);
                await AddUserProfileCache(userModel.userId, userModel);
                this.context.commit("MutationSetUserModel", userModel);
                this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.Success);
                if (routerName == "login") {
                  router.push({ name: "selectList" });
                }
                if (CheckBroadcastIsSupport() && CheckIndexedDBIsSupport()) {
                  await InsertTabIdToTabDB();
                  SetUserChannel(userModel.userId);
                }
                SocketConnect();
              } else {
                this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.None);
                router.push("/SessionExpire");
              }
            });
          } catch (err) {
            this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.None);
            router.push("/SessionExpire");
          }
        }
      }
    } else {
      router.push("/logout");
    }
  }

  @Action
  DoRedirectByApplipcationStore(applipcationStoreType: ApplipcationStoreType) {
    // Open a new window before starting an async call to prevent Safari from blocking window.open()
    const newWindow = window.open("");
    API.AuthenService.DoGetUserInfo().then((resAccess) => {
      if (resAccess.data.responseCode.IsSuccess()) {
        switch (applipcationStoreType) {
          case ApplipcationStoreType.Peak:
            if (newWindow) {
              newWindow.location.href = `${process.env.VUE_APP_PeakEngine}home?emi=${MerchantId().PeakEncryption()}`;
            }
            break;
          case ApplipcationStoreType.NewPeak:
            if (newWindow) {
              newWindow.location.href = `${process.env.VUE_APP_PeakAccount}home_web?emi=${MerchantId().PeakEncryption()}`;
            }
            break;
          case ApplipcationStoreType.Board:
            if (newWindow) {
              newWindow.location.href = `${process.env.VUE_APP_PeakBoard}home?emi=${MerchantId().PeakEncryption()}`;
            }
            break;
          case ApplipcationStoreType.Asset:
            if (newWindow) {
              newWindow.location.href = `${process.env.VUE_APP_PeakAsset}home?emi=${MerchantId().PeakEncryption()}`;
            }
            break;
          case ApplipcationStoreType.Tax:
            if (newWindow) {
              newWindow.location.href = `${process.env.VUE_APP_PeakTax}home?emi=${MerchantId().PeakEncryption()}`;
            }
            break;
        }
      } else {
        router.push("/SessionExpire");
      }
    });
  }
  @Action
  ActivePayroll(merchantId: number) {
    this.context.dispatch("Loading/IsLoading", true, { root: true });
    merchantService.ActivePayroll(merchantId).then(async (resAccess) => {
      if (resAccess.data.responseCode.IsSuccess()) {
        await DeleteSelectedMerchantCache(merchantId);
        window.location.href = `${window.location.origin}/home?emi=${MerchantId().PeakEncryption()}`;
      } else {
        router.push("/SessionExpire");
      }
      this.context.dispatch("Loading/IsLoading", false, { root: true });
    });
  }

  @Action
  public DoGetMerchantList() {
    if (this.merchantListLoadingStatus !== LoadingStatusType.Loading) {
      this.context.dispatch("Loading/IsLoading", true, { root: true });
      this.context.commit("MutationSetMerchantListLoadingStatus", LoadingStatusType.Loading);
      API.MerchantService.DoGetMerchantListByUserId().then((res) => {
        if (res.status.IsSuccess() && res.data.responseCode.IsSuccess()) {
          const merchantList = ConvertToMerchantList(res.data.merchantList);
          this.context.commit("MutationSetMerchantsList", merchantList);
        }
        this.context.commit("MutationSetMerchantListLoadingStatus", LoadingStatusType.Success);
        this.context.dispatch("Loading/IsLoading", false, { root: true });
      });
    }
  }

  @Action
  public async DoSelectMerchant(merchantId: number) {
    if (this.setSelectMerchantLoadingStatus !== LoadingStatusType.Loading) {
      this.context.dispatch("Loading/IsLoading", true, { root: true });
      this.context.commit("MutationsSetSelectMerchantLoadingStatus", LoadingStatusType.Loading);

      const selectedMerchantCache: SelectedMerchantModel | null = await GetSelectedMerchantFromCache(merchantId);

      if (selectedMerchantCache && selectedMerchantCache.merchantId > 0) {
        if (CheckBroadcastIsSupport() && CheckIndexedDBIsSupport()) {
          SetMerchantChannel(selectedMerchantCache.merchantId);
          UpdateTabMerchantId(selectedMerchantCache.merchantId);
        }

        this.context.dispatch("Loading/IsLoading", false, { root: true });
        this.context.commit("MutationsSelectMerchant", selectedMerchantCache);
        this.context.commit("MutationsSetSelectMerchantLoadingStatus", LoadingStatusType.Success);
      } else {
        API.MerchantService.DoGetSelectedMerchant(merchantId).then(async (res) => {
          if (res.status.IsSuccess() && res.data.responseCode.IsSuccess()) {
            const selectMerchant: SelectedMerchantModel = GetSelectedMerchant(res.data.merchant);

            await AddSelectedMerchantCache(selectMerchant.merchantId, selectMerchant);

            if (CheckBroadcastIsSupport() && CheckIndexedDBIsSupport()) {
              UpdateTabMerchantId(selectMerchant?.merchantId);
            }

            this.context.commit("MutationsSelectMerchant", selectMerchant);
          } else {
            this.context.commit("MutationsSelectMerchant", null);
          }
          this.context.dispatch("Loading/IsLoading", false, { root: true });
          this.context.commit("MutationsSetSelectMerchantLoadingStatus", LoadingStatusType.Success);
        });
      }
    }
  }

  @Action
  public DoGetRefreshTokenExpiration() {
    if (this.refreshTokenExpirationLodingStatus !== LoadingStatusType.Loading) {
      const refreshToken = RefreshToken();
      if (refreshToken) {
        this.context.dispatch("Loading/IsLoading", true, { root: true });
        this.context.commit("MutationsSetRefreshTokenExpirationLodingStatus", LoadingStatusType.Loading);
        API.AuthenService.DoGetRefreshTokenExpiration().then((res) => {
          if (res.status.IsSuccess()) {
            const lastUrl = localStorage.getItem("lastUrlPeakPayroll");
            if (lastUrl) {
              localStorage.removeItem("lastUrlPeakPayroll");
              window.location.href = lastUrl;
            } else {
              router.push({ name: "selectList" });
            }
          } else {
            RemoveCookiesToken();
          }
          this.context.dispatch("Loading/IsLoading", false, { root: true });
          this.context.commit("MutationsSetRefreshTokenExpirationLodingStatus", LoadingStatusType.Success);
        });
      } else {
        RemoveCookiesToken();
      }
    }
  }

  @Action
  DoGetFavoriteMerchant() {
    if (this.favoriteMerchantListLoadingStatus !== LoadingStatusType.Loading) {
      this.context.commit("MutationSetFavoriteMerchantListLoadingStatus", LoadingStatusType.Loading);

      API.MerchantService.DoGetFavoriteMerchant().then((res) => {
        if (res.data.responseCode.IsSuccess()) {
          this.context.commit("MutationSetFavoriteMerchantList", res.data.merchantFavorite);
        } else {
          const alertMessage = GetErrorMessageByExceptionCode(res.data.exception);

          this.context.dispatch(
            "Alert/SetAlertMessage",
            {
              alertMessage: alertMessage,
              typeMessage: TypeComponentNoti.error,
            },
            { root: true }
          );
        }
        this.context.commit("MutationSetFavoriteMerchantListLoadingStatus", LoadingStatusType.Success);
      });
    }
  }

  @Action
  DoUpdateFavoriteMerchant(merchantId: number) {
    if (this.updateFavoriteMerchantLoadingStatus !== LoadingStatusType.Loading) {
      this.context.commit("MutationSetUpdateFavoriteMerchantLoadingStatus", LoadingStatusType.Loading);
      API.MerchantService.DoUpdateFavoriteMerchant(merchantId).then((res) => {
        if (res.data.responseCode.IsSuccess()) {
          this.context.commit("MutationSetFavoriteMerchantListLoadingStatus", LoadingStatusType.None);
        } else {
          const alertMessage = GetErrorMessageByExceptionCode(res.data.exception);
          this.context.dispatch(
            "Alert/SetAlertMessage",
            {
              alertMessage: alertMessage,
              typeMessage: TypeComponentNoti.error,
            },
            { root: true }
          );
        }
        this.context.commit("MutationSetUpdateFavoriteMerchantLoadingStatus", LoadingStatusType.Success);
      });
    }
  }

  @Action
  DoDeleteFavoriteMerchant(merchantId: number) {
    if (this.deleteFavoriteMerchantLoadingStatus !== LoadingStatusType.Loading) {
      this.context.commit("MutationSetDeleteFavoriteMerchantLoadingStatus", LoadingStatusType.Loading);

      API.MerchantService.DoDeleteFavoriteMerchant(merchantId).then((res) => {
        if (res.data.responseCode.IsSuccess()) {
          this.context.commit("MutationSetFavoriteMerchantListLoadingStatus", LoadingStatusType.None);
        } else {
          const alertMessage = GetErrorMessageByExceptionCode(res.data.exception);
          this.context.dispatch(
            "Alert/SetAlertMessage",
            {
              alertMessage: alertMessage,
              typeMessage: TypeComponentNoti.error,
            },
            { root: true }
          );
        }
        this.context.commit("MutationSetDeleteFavoriteMerchantLoadingStatus", LoadingStatusType.Success);
      });
    }
  }

  @Action
  DoGetMerchantModelById(merchantId: number): Promise<SelectedMerchantModel | null> {
    return new Promise(async (resolve, reject) => {
      this.context.commit("MutationSetMerchantModelByIdLoadingStatus", LoadingStatusType.Loading);

      const merchantModelOnLocal: { merchant: SelectedMerchantModel } | null =
        this.merchantModelListForMap.get(merchantId) ?? null;

      if (merchantModelOnLocal) {
        setTimeout(() => {
          this.context.commit("MutationSetMerchantModelByIdLoadingStatus", LoadingStatusType.Success);
          resolve({ ...merchantModelOnLocal.merchant });
        }, 0);
      } else {
        const selectedMerchantCache: SelectedMerchantModel | null = await GetSelectedMerchantFromCache(merchantId);

        if (selectedMerchantCache && selectedMerchantCache.merchantId > 0) {
          this.context.commit("MutationSetMerchantModelList", {
            merchantId: merchantId,
            merchant: selectedMerchantCache,
          });

          resolve({ ...selectedMerchantCache });
          this.context.commit("MutationSetMerchantModelByIdLoadingStatus", LoadingStatusType.Success);
        } else {
          API.MerchantService.DoGetSelectedMerchant(merchantId).then(async (res: any) => {
            if (res.status.IsSuccess() && res.data.responseCode.IsSuccess()) {
              const selectedMerchant: SelectedMerchantModel = GetSelectedMerchant(res.data.merchant);
              await AddSelectedMerchantCache(selectedMerchant.merchantId, selectedMerchant);

              this.context.commit("MutationSetMerchantModelList", {
                merchantId: merchantId,
                merchant: selectedMerchant,
              });
              resolve({ ...selectedMerchant });
            } else {
              this.context.commit("MutationSetMerchantModelList", { merchantId: merchantId, merchant: null });
              resolve(null);
            }
            this.context.commit("MutationSetMerchantModelByIdLoadingStatus", LoadingStatusType.Success);
          });
        }
      }
    });
  }

  @Action
  DoIntrospectAccessToken(): Promise<boolean> {
    return new Promise((resolve) => {
      API.AuthenService.DoIntrospectAccessToken().then((res: any) => {
        if (res.status.IsSuccess() && res.data.active) {
          resolve(true);
        } else {
          resolve(false);
        }
      });
    });
  }

  @Action
  public async DoGetUserInfoByResetCache(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      this.context.dispatch("Loading/IsLoading", true, { root: true });
      this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.Loading);

      API.AuthenService.DoGetUserInfo().then(async (res) => {
        if (res.status.IsSuccess()) {
          const userModel: UserModel = ConvertToUserModel(res.data.userInfo);
          await AddUserProfileCache(userModel.userId, userModel);

          this.context.commit("MutationSetUserModel", userModel);
        }

        this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.Success);
        this.context.dispatch("Loading/IsLoading", false, { root: true });
        resolve();
      });
    });
  }

  @Action
  public async DoGetUserInfoForInvalidPIN(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      this.context.commit("MutationSetGetUserInfoForInvalidPINLoadingStatus", LoadingStatusType.Loading);

      API.AuthenService.DoGetUserInfo()
        .then(async (res) => {
          if (res.status.IsSuccess()) {
            const userModel: UserModel = ConvertToUserModel(res.data.userInfo);
            await AddUserProfileCache(userModel.userId, userModel);

            this.context.commit("MutationSetUserModel", userModel);
            this.context.commit("MutationSetGetUserInfoForInvalidPINLoadingStatus", LoadingStatusType.Success);
          } else {
            this.context.commit("MutationSetGetUserInfoForInvalidPINLoadingStatus", LoadingStatusType.Fail);
          }
          resolve();
        })
        .catch((error) => {
          this.context.commit("MutationSetGetUserInfoForInvalidPINLoadingStatus", LoadingStatusType.Fail);
          resolve();
        });
    });
  }

  @Action
  public async DoGetMerchantByResetCache({
    merchantId,
    isUseMainDatabase,
  }: any): Promise<SelectedMerchantModel | null> {
    return new Promise(async (resolve, reject) => {
      this.context.dispatch("Loading/IsLoading", true, { root: true });
      this.context.commit("MutationsGetMerchantByResetCacheLoadingStatus", LoadingStatusType.Loading);

      const selectedMerchantCache: SelectedMerchantModel | null = await GetSelectedMerchantFromCache(merchantId);

      if (selectedMerchantCache && selectedMerchantCache.merchantId > 0) {
        this.context.commit("MutationsSelectMerchant", selectedMerchantCache);

        setTimeout(() => {
          this.context.dispatch("Loading/IsLoading", false, { root: true });
          this.context.commit("MutationsGetMerchantByResetCacheLoadingStatus", LoadingStatusType.Success);

          resolve(selectedMerchantCache);
        }, 250);
      } else {
        API.MerchantService.DoGetSelectedMerchant(merchantId).then(async (res) => {
          let selectMerchant: SelectedMerchantModel | null = null;

          if (res.status.IsSuccess() && res.data.responseCode.IsSuccess()) {
            selectMerchant = GetSelectedMerchant(res.data.merchant);
            await AddSelectedMerchantCache(selectMerchant.merchantId, selectMerchant);
          }

          this.context.commit("MutationsSelectMerchant", selectMerchant);
          this.context.commit("MutationsGetMerchantByResetCacheLoadingStatus", LoadingStatusType.Success);
          this.context.dispatch("Loading/IsLoading", false, { root: true });

          resolve(selectMerchant);
        });
      }
    });
  }

  @Action
  public async verifySignInOTP({ otp, referenceOtp, twoFactorSecretKey, recoveryCode }: any): Promise<void> {
    return new Promise(async (resolve) => {
      this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.Loading);

      try {
        // ใช้ข้อมูล login request ต้นฉบับ + เพิ่ม otp และ reference_otp
        const originalRequest = this.originalLoginRequest;

        // ส่งข้อมูลตามประเภท 2FA
        let finalReferenceOtp = referenceOtp;
        let finalTwoFactorSecretKey = twoFactorSecretKey;

        // ถ้าเป็น Authenticator และมี twoFactorSecretKey ให้ใช้แทน referenceOtp
        if (twoFactorSecretKey && !referenceOtp) {
          finalReferenceOtp = "";
          finalTwoFactorSecretKey = twoFactorSecretKey;
        }

        // สร้าง request input ใหม่ที่มี OTP
        const requestInput = {
          userName: originalRequest.username,
          password: originalRequest.password,
          userType: originalRequest.userType,
          iss: originalRequest.iss || "",
        };

        const source = ActivityLogSource.PeakPayroll;
        const response = await API.AuthenService.DoGetAccessToken(
          requestInput,
          source,
          recoveryCode ? "" : otp, // ถ้าใช้ recoveryCode ไม่ต้องส่ง otp
          recoveryCode ? "" : finalReferenceOtp, // ถ้าใช้ recoveryCode ไม่ต้องส่ง referenceOtp
          false, // regenerate_otp = false สำหรับ verify OTP
          recoveryCode ? "" : finalTwoFactorSecretKey, // ถ้าใช้ recoveryCode ไม่ต้องส่ง twoFactorSecretKey
          recoveryCode || "" // ส่ง recoveryCode ถ้ามี
        );

        // เก็บ loginResponseData สำหรับ recovery code flow
        this.context.commit("MutationSetLoginResponseData", response.data);

        // ตรวจสอบ invalid_recovery_code ก่อน
        if (response.data && response.data.invalid_recovery_code === true) {
          const errorMessage = i18n.t("Security_Setting.Modal_Two_Factor_Authentication_Recovery_Code_Error") as string;
          this.context.commit("MutationSetTwoFactorErrorMessage", errorMessage);

          // อัปเดต recoveryCodeAttempt ใน twoFactorAuthData
          if (response.data.recovery_code_attempt !== undefined) {
            const currentTwoFactorData = this.twoFactorAuthData || {};
            this.context.commit("MutationSetTwoFactorAuthData", {
              ...currentTwoFactorData,
              recoveryCodeAttempt: response.data.recovery_code_attempt,
            });
          }

          // เก็บ verify_next_delay สำหรับการนับถอยหลัง
          if (response.data.verify_next_delay && response.data.verify_next_delay > 0) {
            this.context.commit("MutationSetVerifyNextDelay", response.data.verify_next_delay);
          }
          this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.None);
          resolve();
          return;
        }

        // ตรวจสอบ invalid_otp และ responseCode ก่อน
        if (response.data && response.data.invalid_otp === true) {
          let errorMessage = "";

          // ตรวจสอบ responseCode เพื่อแยกประเภท error
          const responseCode = response.data.response_code
            ? response.data.response_code
            : AuthenticationResponseCode.OK;

          if (responseCode === AuthenticationResponseCode.EmailOTPExpired) {
            // OTP หมดอายุ
            errorMessage = i18n.t("Security_Setting.Modal_Two_Factor_Authentication_OTP_Expired") as string;
          } else {
            // OTP ผิด
            errorMessage = i18n.t("Security_Setting.Modal_Two_Factor_Authentication_Enter_Code_Error_OTP") as string;
          }

          // แสดง error message และไม่ปิด modal
          this.context.commit("MutationSetTwoFactorErrorMessage", errorMessage);

          // เก็บ verify_next_delay สำหรับการนับถอยหลัง
          if (response.data.verify_next_delay && response.data.verify_next_delay > 0) {
            this.context.commit("MutationSetVerifyNextDelay", response.data.verify_next_delay);
          }
          this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.None);
          resolve();
          return;
        }

        if (response.status.IsSuccess()) {
          const responseCode = response.data.response_code
            ? response.data.response_code
            : AuthenticationResponseCode.OK;

          // ตรวจสอบ DisabledTwoFactor (305) - recovery code valid และ 2FA จะถูกปิด
          if (responseCode === AuthenticationResponseCode.DisabledTwoFactor) {
            // เก็บข้อมูล response สำหรับ confirmation step
            this.context.commit("MutationSetLoginResponseData", response.data);
            this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.None);
            resolve();
            return;
          }

          if (responseCode.IsSuccess()) {
            // Login สำเร็จ
            await AddAccessTokenCache(response.data.access_token);

            if (originalRequest.keepLogin) {
              SetCookiesKeepLogin();
              SetCookiesAccessToken(response.data.access_token);
              SetCookiesRefreshToken(response.data.refresh_token, true);
            } else {
              SetCookiesAccessToken(response.data.access_token);
              SetCookiesRefreshToken(response.data.refresh_token);
            }

            API.AuthenService.DoGetUserInfo().then(async (resUserInfo) => {
              if (resUserInfo.status.IsSuccess()) {
                // ลบ reference_otp cookie เมื่อ login สำเร็จ
                if (originalRequest.username) {
                  const { removeReferenceOtpCookie } = await import("@/helper/twoFactorAuthHelper");
                  removeReferenceOtpCookie(originalRequest.username);
                }

                DatadogRumService.SetUserFromUserInfo(resUserInfo?.data?.userInfo);
                const userModel: UserModel = ConvertToUserModel(resUserInfo.data.userInfo);
                await AddUserProfileCache(userModel.userId, userModel);
                this.context.commit("MutationSetUserModel", userModel);
                this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.Success);
                ClearMasterTabFromCookies();
                if (CheckBroadcastIsSupport() && CheckIndexedDBIsSupport()) {
                  await InsertTabIdToTabDB();
                  SetUserChannel(userModel.userId);
                  BroadcastReCreateTab();
                }
                const lastUrl = localStorage.getItem("lastUrlPeakPayroll");
                if (lastUrl) {
                  ClearMasterTabFromCookies();
                  localStorage.removeItem("lastUrlPeakPayroll");
                  window.location.href = lastUrl;
                } else {
                  router.push({ name: "selectList" });
                }
              } else {
                this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.Success);
                router.push("/logout");
              }
            });
          } else {
            // OTP ไม่ถูกต้อง
            this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.Fail);
            this.context.commit(
              "MutationSetTwoFactorErrorMessage",
              i18n.t("Security_Setting.Modal_Two_Factor_Authentication_Enter_Code_Error_OTP")
            );
          }
        } else {
          this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.Fail);
          this.context.commit(
            "MutationSetTwoFactorErrorMessage",
            i18n.t("Security_Setting.Modal_Two_Factor_Authentication_Enter_Code_Error_OTP")
          );
        }
      } catch (error) {
        console.error("Verify OTP Error:", error);
        this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.Fail);
        this.context.commit(
          "MutationSetTwoFactorErrorMessage",
          i18n.t("Security_Setting.Modal_Two_Factor_Authentication_Enter_Code_Error_OTP")
        );
      }

      resolve();
    });
  }

  @Action
  public async regenerateSignInOTP(): Promise<void> {
    return new Promise(async (resolve) => {
      this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.Loading);
      this.context.dispatch("Loading/IsLoading", true, { root: true });

      try {
        // ใช้ข้อมูล login request ต้นฉบับ + เพิ่ม regenerate_otp = true
        const originalRequest = this.originalLoginRequest;

        const source = ActivityLogSource.PeakTax;
        const response = await API.AuthenService.DoGetAccessToken(
          {
            userName: originalRequest.username,
            password: originalRequest.password,
            userType: originalRequest.userType,
            iss: originalRequest.iss || "",
          },
          source,
          undefined, // ไม่มี otp
          undefined, // ไม่มี reference_otp
          true // regenerate_otp = true
        );

        // ตรวจสอบ response_code 201 ที่มี reference_otp ใหม่
        const responseCode = response.data.response_code ? response.data.response_code : AuthenticationResponseCode.OK;

        if (responseCode === AuthenticationResponseCode.RequireEmailOTP) {
          // อัพเดท reference_otp ใหม่
          const twoFactorData = {
            type: TwoFactorAuthenticationType.Email,
            referenceOtp: response.data.reference_otp || "",
          };
          this.context.commit("MutationSetTwoFactorAuthData", twoFactorData);

          // เก็บ reference_otp ใหม่ใน cookie
          if (response.data.reference_otp && response.data.reference_otp_live) {
            const { setReferenceOtpCookie } = await import("@/helper/twoFactorAuthHelper");
            setReferenceOtpCookie(
              originalRequest.username,
              response.data.reference_otp,
              response.data.reference_otp_live
            );
          }
        } else if (responseCode === AuthenticationResponseCode.EmailOTPAlreadyRequested) {
          // EmailOTPAlreadyRequested - ตรวจสอบ cookie สำหรับ reference_otp
          let referenceOtp = response.data.reference_otp;
          if (!referenceOtp || referenceOtp === null) {
            const { getReferenceOtpCookie, isReferenceOtpValid } = await import("@/helper/twoFactorAuthHelper");
            const cookieData = getReferenceOtpCookie(originalRequest.username);
            if (cookieData && isReferenceOtpValid(originalRequest.username)) {
              referenceOtp = cookieData.referenceOtp;
            }
          }

          // อัพเดท reference_otp จาก cookie
          const twoFactorData = {
            type: TwoFactorAuthenticationType.Email,
            referenceOtp: referenceOtp || "",
          };
          this.context.commit("MutationSetTwoFactorAuthData", twoFactorData);
        }
      } catch (error) {
        console.error("Regenerate OTP Error:", error);
      } finally {
        this.context.dispatch("Loading/IsLoading", false, { root: true });
      }

      this.context.commit("MutationLoginLoadingStatus", LoadingStatusType.None);
      resolve();
    });
  }

  @Action
  public SetTwoFactorAuthData(data: any): void {
    this.context.commit("MutationSetTwoFactorAuthData", data);
  }

  @Action
  public ClearTwoFactorErrorMessage(): void {
    this.context.commit("MutationSetTwoFactorErrorMessage", "");
  }

  @Action
  public SetVerifyNextDelay(delay: number): void {
    this.context.commit("MutationSetVerifyNextDelay", delay);
  }
}

export default LogIn;
